"use client";

import { useCustomer } from "@/contexts/customer-context";
import { CartDropdown } from "@/modules/cart/components/cart-dropdown";
import { Button } from "@/modules/common/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/modules/common/components/dropdown-menu";
import { UserMenu } from "@/modules/layout/components/user-menu";
import { NotificationMenu } from "@/modules/notifications/components/notification-menu";
import { useCategories } from "@/modules/products/hooks/useCategories";
import { useCart } from "@/src/contexts/cart-context";
import { useCartStore, useRegionStore } from "@/store/index";
import { DEFAULT_COUNTRY_CODE } from "@/lib/constants";
import { ChevronDown, Heart, Menu, Search, ShoppingBag } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
import { HeaderMobileMenu } from "./header-mobile-menu";
import { LocationSelector } from "./location-selector";

interface IProps {
  countryCode?: string;
}

const PRODUCT_SEACH_PARAM_KEY = "q";
export function Header({ countryCode }: IProps) {
  countryCode ??= DEFAULT_COUNTRY_CODE;

  const router = useRouter();
  const params = useSearchParams();

  const { isCartOpen, toggleCart, closeCart } = useCartStore();
  const { cart } = useCart();
  const { customer } = useCustomer();
  const { data } = useCategories();

  // Prefer the stored selection for the logo so it always points home to the
  // user's country, even in the brief window before CountryCodeSync heals a
  // stale URL segment.
  const storeCountryCode = useRegionStore((s) => s.countryCode);
  const logoCountry = storeCountryCode || countryCode;

  const categories = data?.response?.product_categories;

  const [searchTerm, setSearchTerm] = useState(
    params.get(PRODUCT_SEACH_PARAM_KEY) ?? ""
  );

  // Calculate items count
  const itemCount =
    cart?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  const goTo = useCallback(
    (path: string) => router.push(`/${countryCode}/${path}`),
    [countryCode, router]
  );
  const handleSearch = () => {
    const newParams = new URLSearchParams(params.toString());
    if (searchTerm.trim()) {
      newParams.set(PRODUCT_SEACH_PARAM_KEY, searchTerm);
    } else {
      newParams.delete(PRODUCT_SEACH_PARAM_KEY);
    }
    goTo(`products/?${newParams.toString()}`);
  };

  const handleNavigateCategory = (id: string) =>
    router.push(
      `/${countryCode}/products?category_id=${id}&filter=departamentos`
    );

  const Logo = (
    <Link href={`/${logoCountry}`} className="flex items-center gap-2">
      <div className="relative h-10 w-32 md:h-12 md:w-40">
        <Image
          src="/logo1.svg"
          alt="Carambola Logo"
          fill
          className="object-contain object-left"
          priority
        />
      </div>
    </Link>
  );

  return (
    <>
      <header className="bg-cream sticky top-3 z-40 mx-auto grid rounded-xl p-3 shadow-md transition-all duration-100">
        <div className="flex items-center justify-center md:hidden">{Logo}</div>
        <div className="flex w-full items-center justify-between gap-2 place-self-center px-4 md:gap-4 md:px-4">
          {/* Mobile Menu */}
          <div className="lg:hidden">
            <HeaderMobileMenu />
          </div>

          {/* Logo */}
          <div className="hidden lg:block">{Logo}</div>

          {/* Category selection Menu */}
          <div className="text-green-main hidden items-center gap-2 font-medium lg:flex">
            <span className="hidden xl:block">Categorías</span>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-green-main hover:bg-green-main/10 flex w-20 items-center gap-2"
                >
                  <Menu className="h-6 w-6" />
                  <ChevronDown className="h-6 w-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-cream w-64">
                {categories?.map((cat) => (
                  <DropdownMenuItem
                    onClick={() => handleNavigateCategory(cat.id)}
                    className="text-green-main text-base"
                    key={cat.id}
                  >
                    {cat.name}
                  </DropdownMenuItem>
                ))}
                {/* <DropdownMenuLabel>Subcategorias</DropdownMenuLabel> */}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Search Bar - Hidden on mobile */}
          <div className="hidden max-w-xl flex-1 md:block">
            <div className="relative">
              <input
                type="search"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={({ target }) => setSearchTerm(target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="border-green-main font-inter focus:ring-green-main/20 w-full rounded-full border-2 bg-white px-6 py-2 pr-12 text-sm text-gray-900 placeholder-gray-500 focus:ring-2 focus:outline-none"
              />
              <Button
                size="icon"
                onClick={handleSearch}
                className="bg-green-main absolute top-1/2 right-1 aspect-square h-8 w-8 -translate-y-1/2 rounded-full p-0 hover:bg-[#047a2e]"
              >
                <Search className="h-4 w-4 text-white" />
              </Button>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            {/* Region Selector */}
            <div className="hidden lg:block">
              <LocationSelector />
            </div>

            {/* Currency Selector */}
            {/* <div className="hidden lg:block">
              <CurrencySelector />
            </div> */}

            {/* Wishlist */}
            {customer && (
              <Button
                variant="ghost"
                size="icon"
                className="text-green-main hover:bg-green-main/10 lg:flex"
                onClick={() => {
                  goTo(`profile/favorites`);
                }}
              >
                <Heart className="h-5 w-5" />
              </Button>
            )}

            {/* Notifications */}
            {customer && <NotificationMenu />}

            {/* Cart */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCart}
                className="text-green-main hover:bg-green-main/10 relative"
              >
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="bg-orange-main absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white">
                    {itemCount}
                  </span>
                )}
              </Button>
              <CartDropdown isOpen={isCartOpen} onClose={closeCart} />
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
        {/* Search Bar - Hidden on mobile */}
        <div className="max-w-xl flex-1 md:hidden pt-3">
          <div className="relative">
            <input
              type="search"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={({ target }) => setSearchTerm(target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="border-green-main font-inter focus:ring-green-main/20 w-full rounded-full border-2 bg-white px-6 py-2 pr-12 text-sm text-gray-900 placeholder-gray-500 focus:ring-2 focus:outline-none"
            />
            <Button
              size="icon"
              onClick={handleSearch}
              className="bg-green-main absolute top-1/2 right-1 aspect-square h-8 w-8 -translate-y-1/2 rounded-full p-0 hover:bg-[#047a2e]"
            >
              <Search className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
      </header>
    </>
  );
}
